import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  Slider,
} from "@mui/material";
import ReactWebcam from "react-webcam";

const PhotoCaptureModal = ({ open, onClose, onCapture }) => {
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [currentZoom, setCurrentZoom] = useState(1);
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/i.test(navigator.userAgent);

  const clampZoom = useCallback((value) => {
    const min = Number.isFinite(zoomRange.min) ? zoomRange.min : 1;
    const max = Number.isFinite(zoomRange.max) ? zoomRange.max : min;
    return Math.min(max, Math.max(min, value));
  }, [zoomRange.max, zoomRange.min]);

  const applyZoom = useCallback(async (nextZoom) => {
    const stream = streamRef.current;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;

    const clamped = clampZoom(nextZoom);
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
      setCurrentZoom(clamped);
    } catch (err) {
      console.warn("Failed to apply camera zoom:", err);
    }
  }, [clampZoom]);

  const stopActiveStreamTracks = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return;
    setZoomSupported(false);
    setZoomRange({ min: 1, max: 1, step: 0.1 });
    setCurrentZoom(1);

    const checkCameraPermission = async () => {
      try {
        if (navigator.permissions) {
          const status = await navigator.permissions.query({ name: "camera" });
          if (status.state === "denied") setPermissionDenied(true);

          status.onchange = () => {
            setPermissionDenied(status.state === "denied");
          };
        }
      } catch (err) {
        console.warn("Permission check failed:", err);
      }
    };

    checkCameraPermission();
  }, [open]);

  useEffect(() => {
    if (open) return undefined;
    stopActiveStreamTracks();
    return undefined;
  }, [open, stopActiveStreamTracks]);

  useEffect(() => () => stopActiveStreamTracks(), [stopActiveStreamTracks]);

  const handleCapture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) onCapture(imageSrc);
  };

  const handleUserMedia = useCallback((stream) => {
    streamRef.current = stream;
    const videoTrack = stream?.getVideoTracks?.()[0];
    if (!videoTrack) return;

    const capabilities = videoTrack.getCapabilities?.() || {};
    const settings = videoTrack.getSettings?.() || {};
    const canZoom = typeof capabilities.zoom === "object" || Number.isFinite(capabilities.zoom?.max);
    if (!canZoom) {
      setZoomSupported(false);
      setZoomRange({ min: 1, max: 1, step: 0.1 });
      setCurrentZoom(1);
      return;
    }

    const min = Number.isFinite(capabilities.zoom?.min) ? capabilities.zoom.min : 1;
    const max = Number.isFinite(capabilities.zoom?.max) ? capabilities.zoom.max : min;
    const step = Number.isFinite(capabilities.zoom?.step) ? capabilities.zoom.step : 0.1;
    const supportsZoomRange = max > min;
    const initial = Number.isFinite(settings.zoom) ? settings.zoom : min;
    const normalizedInitial = Math.min(max, Math.max(min, supportsZoomRange ? min : initial));

    setZoomSupported(supportsZoomRange);
    setZoomRange({ min, max, step });
    setCurrentZoom(normalizedInitial);
    if (supportsZoomRange) {
      videoTrack
        .applyConstraints({ advanced: [{ zoom: normalizedInitial }] })
        .catch((err) => {
          console.warn("Failed to apply initial camera zoom:", err);
        });
    }
  }, []);

  const handleZoomSliderChange = useCallback((_, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (!Number.isFinite(next)) return;
    applyZoom(next);
  }, [applyZoom]);

  const toggleFacingMode = () =>
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 2,
          width: "90%",
          maxWidth: 500,
          borderRadius: 1,
        }}
      >
        {permissionDenied ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              Camera access is blocked. Please allow camera permission in your
              browser settings to continue.
            </Alert>

            <Typography variant="body2" fontWeight="bold" mb={1}>
              How to enable:
            </Typography>

            {isIOS && (
              <Typography variant="body2" gutterBottom>
                📱 <strong>iOS Safari:</strong> Settings → Safari → Camera → Allow
              </Typography>
            )}

            {isAndroid && (
              <Typography variant="body2" gutterBottom>
                📱 <strong>Android Chrome:</strong> Chrome (⋮) → Settings → Site
                settings → Camera → Allow
              </Typography>
            )}

            {!isIOS && !isAndroid && (
              <Typography variant="body2" gutterBottom>
                ⚙️ <strong>Browser:</strong> Check browser settings → Site
                Permissions → Camera → Allow
              </Typography>
            )}

            <Button onClick={onClose} variant="contained" sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Capture Photo
            </Typography>

            <Box sx={{ width: "100%", position: "relative" }}>
              <ReactWebcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode }}
                style={{ width: "100%" }}
                onUserMedia={handleUserMedia}
                onUserMediaError={() => setPermissionDenied(true)}
              />
              {zoomSupported && (
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    top: 12,
                    right: 10,
                    bottom: 12,
                    width: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    backgroundColor: "rgba(0,0,0,0.35)",
                    backdropFilter: "blur(2px)",
                    WebkitBackdropFilter: "blur(2px)",
                    zIndex: 2,
                    "& .MuiSlider-root": {
                      height: "calc(100% - 20px)",
                      color: theme.colors.white,
                    },
                    "& .MuiSlider-thumb": {
                      width: 14,
                      height: 14,
                    },
                    "& .MuiSlider-rail": {
                      opacity: 0.4,
                    },
                  })}
                >
                  <Slider
                    orientation="vertical"
                    min={zoomRange.min}
                    max={zoomRange.max}
                    step={zoomRange.step || 0.1}
                    value={currentZoom}
                    onChange={handleZoomSliderChange}
                    aria-label="Camera zoom"
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {isMobile && (
                <Button
                  onClick={toggleFacingMode}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    px: 3,
                    py: 1.25,
                  }}>
                  Switch Camera
                </Button>
              )}
              <Button
                onClick={handleCapture}
                variant="contained"
                sx={{
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                }}
              >
                Capture Photo
              </Button>
              <Button
                onClick={onClose}
                variant="text"
                sx={{
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default PhotoCaptureModal;
